import {
  ClusterOutlined,
  NodeIndexOutlined,
  ProfileOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  AgenticRequestorRule,
  IdpGroups,
  RequestorRule,
  WifRequestorRule,
} from "@p0security/shared/types/workflow/types";
import { App, Flex, Input, Select, Tooltip } from "antd";
import { useMcpClients } from "components/Assessment/components/AiInventory/hooks/useMcpClients";
import { FormRow, RowLabel } from "components/common/FormRow";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { validate as validateEmail } from "isemail";
import { omit } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { RoutingRuleEditorContext } from "../../store/RoutingRuleEditorContext";
import { newAgentRule, newRequestorRuleOfType } from "../../store/mutations";
import { IdpGroupInput } from "../IdpGroupInput";

type AgenticUser = AgenticRequestorRule["user"];
type AgenticUserType = AgenticUser["type"];
type AgentRule = AgenticRequestorRule["agent"];
type AgentType = AgentRule["type"];

/** `group` is nested under `user` in the UI; in the data model they are siblings. */
type IdentityKind = "agentic" | "any" | "user" | "wif";

const IDENTITY_OPTIONS: PillSelectOption<IdentityKind>[] = [
  {
    icon: <TeamOutlined />,
    label: "Any",
    description: "Match any identity",
    value: "any",
  },
  {
    icon: <UserOutlined />,
    label: "User",
    description: "A specific human, by email or directory group",
    value: "user",
  },
  {
    icon: <NodeIndexOutlined />,
    label: "Agentic",
    description: "A request brokered by an AI agent",
    value: "agentic",
  },
  {
    icon: <ClusterOutlined />,
    label: "Workload identity",
    description: "A non-human identity authenticating via a GCP WIF pool",
    value: "wif",
  },
];

type UserSubKind = "group" | "user";

const USER_SUB_OPTIONS: PillSelectOption<UserSubKind>[] = [
  {
    description: "Match a single user by email",
    icon: <UserOutlined />,
    label: "Specific user",
    value: "user",
  },
  {
    description: "Match anyone in a directory group",
    icon: <ProfileOutlined />,
    label: "Directory group",
    value: "group",
  },
];

const AGENTIC_USER_OPTIONS: PillSelectOption<AgenticUserType>[] = [
  {
    icon: <TeamOutlined />,
    label: "Any identity",
    value: "any",
  },
  {
    description: "Match a single user by email",
    icon: <UserOutlined />,
    label: "Specific user",
    value: "user",
  },
  {
    description: "Match anyone in a directory group",
    icon: <ProfileOutlined />,
    label: "Directory group",
    value: "group",
  },
  {
    description: "A workload identity authenticating via a GCP WIF pool",
    icon: <ClusterOutlined />,
    label: "Workload identity",
    value: "wif",
  },
];

const AGENT_OPTIONS: PillSelectOption<AgentType>[] = [
  {
    icon: <TeamOutlined />,
    label: "Any agent",
    value: "any",
  },
  {
    description: "Match by MCP client id",
    icon: <NodeIndexOutlined />,
    label: "A specific MCP client",
    value: "agent",
  },
  {
    description: "Match agents owned by a specific user",
    icon: <UserOutlined />,
    label: "Owned by a specific user",
    value: "agent-owner",
  },
];

const INLINE_TEXT_INPUT_CLASS = "flex-[1_1_360px] max-w-xl min-w-3xs";

const requestorHasSubselections = (
  requestor: RequestorRule | undefined
): boolean => {
  if (!requestor) return false;
  switch (requestor.type) {
    case "any":
      return false;
    case "user":
      return !!requestor.uid;
    case "group":
      return requestor.groups.some((g) => !!g.id);
    case "agentic":
      return requestor.agent.type !== "any" || requestor.user.type !== "any";
    case "wif":
      return !!requestor.parent || !!requestor.pool || !!requestor.subject;
    default: {
      const _validateExhaustion: never = requestor;
      return false;
    }
  }
};

const identityKindOf = (requestor: RequestorRule | undefined): IdentityKind => {
  if (!requestor) return "any";
  switch (requestor.type) {
    case "any":
      return "any";
    case "user":
    case "group":
      return "user";
    case "agentic":
      return "agentic";
    case "wif":
      return "wif";
    default: {
      const _validateExhaustion: never = requestor;
      return "any";
    }
  }
};

const UserInlineEditor: React.FC<{
  value: Extract<AgenticUser, { type: "group" } | { type: "user" }>;
  onChange: (
    next: Extract<AgenticUser, { type: "group" } | { type: "user" }>
  ) => void;
  setRequestorError: (error: string | undefined) => void;
}> = ({ value, onChange, setRequestorError }) => {
  const [emailBlurred, setEmailBlurred] = useState(false);

  useEffect(() => {
    setEmailBlurred(false);
  }, [value.type]);

  useEffect(
    () => () => {
      setRequestorError(undefined);
    },
    [setRequestorError]
  );

  useEffect(() => {
    if (value.type !== "user" || !emailBlurred) {
      setRequestorError(undefined);
      return;
    }

    if (value.uid && !validateEmail(value.uid)) {
      setRequestorError("Invalid email address format.");
    } else {
      setRequestorError(undefined);
    }
  }, [setRequestorError, value, emailBlurred]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value.type !== "user") return;
      onChange({ ...value, uid: e.target.value });
    },
    [value, onChange]
  );

  const handleEmailBlur = useCallback(() => setEmailBlurred(true), []);

  const handleGroupChange = useCallback(
    (next: IdpGroups) => onChange(next),
    [onChange]
  );

  if (value.type === "user") {
    const invalid = emailBlurred && !!value.uid && !validateEmail(value.uid);
    return (
      <Tooltip
        title={invalid ? "Invalid email address format." : undefined}
        open={invalid ? undefined : false}
      >
        <Input
          value={value.uid}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          placeholder="someone@yourorg.com"
          prefix={<UserOutlined />}
          status={invalid ? "error" : undefined}
          className={INLINE_TEXT_INPUT_CLASS}
        />
      </Tooltip>
    );
  }
  return (
    <IdpGroupInput
      value={value}
      onChange={handleGroupChange}
      formItemProps={{
        colon: false,
        layout: "horizontal",
        style: { marginBottom: 0, maxWidth: 500 },
      }}
    />
  );
};

const AgentInlineEditor: React.FC<{
  value: AgentRule;
  onChange: (next: AgentRule) => void;
}> = ({ value, onChange }) => {
  const mcpClients = useMcpClients();

  const clientOptions = useMemo(
    () =>
      mcpClients.clients.map((c) => ({
        value: c.id,
        label: `${c.createdBy?.user ? `${c.createdBy.user}'s ` : ""}${
          c.platform
        } on ${c.hostname}`,
      })),
    [mcpClients.clients]
  );

  const handleAzpChange = useCallback(
    (azp: string) => {
      if (value.type !== "agent") return;
      onChange({ ...value, azp });
    },
    [value, onChange]
  );
  const handleOwnerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value.type !== "agent-owner") return;
      onChange({ ...value, owner: e.target.value });
    },
    [value, onChange]
  );

  switch (value.type) {
    case "any":
      return null;
    case "agent":
      return (
        <Select
          value={value.azp || undefined}
          onChange={handleAzpChange}
          options={clientOptions}
          loading={mcpClients.loading}
          placeholder="Select an MCP client..."
          showSearch
          optionFilterProp="label"
          className={INLINE_TEXT_INPUT_CLASS}
        />
      );
    case "agent-owner":
      return (
        <Input
          value={value.owner}
          onChange={handleOwnerChange}
          placeholder="owner@yourorg.com"
          prefix={<UserOutlined />}
          className={INLINE_TEXT_INPUT_CLASS}
        />
      );
    default: {
      const _validateExhaustion: never = value;
      return null;
    }
  }
};

const AgenticEditor: React.FC<{
  value: AgenticRequestorRule;
  onChange: (next: AgenticRequestorRule) => void;
  setRequestorError: (error: string | undefined) => void;
}> = ({ value, onChange, setRequestorError }) => {
  const handleAgentType = useCallback(
    (next: AgentType) => onChange({ ...value, agent: newAgentRule(next) }),
    [value, onChange]
  );
  const handleAgent = useCallback(
    (next: AgentRule) => onChange({ ...value, agent: next }),
    [value, onChange]
  );
  const handleUserType = useCallback(
    (next: AgenticUserType) =>
      onChange({
        ...value,
        user: newRequestorRuleOfType(next) as AgenticUser,
      }),
    [value, onChange]
  );
  const handleUser = useCallback(
    (next: AgenticUser) => onChange({ ...value, user: next }),
    [value, onChange]
  );

  return (
    <Flex vertical gap={8} className="w-full">
      <FormRow>
        <RowLabel>Agent</RowLabel>
        <PillSelect
          value={value.agent.type}
          options={AGENT_OPTIONS}
          onChange={handleAgentType}
        />
        <AgentInlineEditor value={value.agent} onChange={handleAgent} />
      </FormRow>
      <FormRow>
        <RowLabel>User</RowLabel>
        <PillSelect
          value={value.user.type}
          options={AGENTIC_USER_OPTIONS}
          onChange={handleUserType}
        />
        {value.user.type === "user" || value.user.type === "group" ? (
          <UserInlineEditor
            value={value.user}
            onChange={handleUser}
            setRequestorError={setRequestorError}
          />
        ) : value.user.type === "wif" ? (
          <WifEditor
            value={value.user}
            onChange={handleUser}
            setRequestorError={setRequestorError}
          />
        ) : null}
      </FormRow>
    </Flex>
  );
};

const WifEditor: React.FC<{
  value: WifRequestorRule;
  onChange: (next: WifRequestorRule) => void;
  setRequestorError: (error: string | undefined) => void;
}> = ({ value, onChange, setRequestorError }) => {
  useEffect(
    () => () => {
      setRequestorError(undefined);
    },
    [setRequestorError]
  );

  const handleBlur = useCallback(() => {
    if (!value.parent) {
      setRequestorError("A project number is required.");
    } else if (!value.pool) {
      setRequestorError("A pool ID is required.");
    } else {
      setRequestorError(undefined);
    }
  }, [value.parent, value.pool, setRequestorError]);

  const handleParentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, parent: e.target.value }),
    [value, onChange]
  );
  const handlePoolChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, pool: e.target.value }),
    [value, onChange]
  );
  const handleSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const subject = e.target.value;
      onChange(subject ? { ...value, subject } : omit(value, "subject"));
    },
    [value, onChange]
  );

  return (
    <Flex vertical gap={8} className="w-full">
      <FormRow>
        <RowLabel>Project number</RowLabel>
        <Input
          value={value.parent}
          onChange={handleParentChange}
          onBlur={handleBlur}
          placeholder="123456789012"
          className={INLINE_TEXT_INPUT_CLASS}
        />
      </FormRow>
      <FormRow>
        <RowLabel>WIF pool ID</RowLabel>
        <Input
          value={value.pool}
          onChange={handlePoolChange}
          onBlur={handleBlur}
          placeholder="my-pool"
          className={INLINE_TEXT_INPUT_CLASS}
        />
      </FormRow>
      <FormRow>
        <RowLabel>Identity subject</RowLabel>
        <Input
          value={value.subject ?? ""}
          onChange={handleSubjectChange}
          placeholder="Optional; unanchored regex"
          className={INLINE_TEXT_INPUT_CLASS}
        />
      </FormRow>
    </Flex>
  );
};

export const RequestorEditor: React.FC = () => {
  const { requestor, setRequestor, setRequestorError } = useContext(
    RoutingRuleEditorContext
  );
  const { modal } = App.useApp();

  const identityKind = identityKindOf(requestor);

  const handleIdentityChange = useCallback(
    (next: IdentityKind) => {
      const concrete: RequestorRule["type"] = next === "user" ? "user" : next;
      const apply = () => setRequestor(newRequestorRuleOfType(concrete));
      if (requestorHasSubselections(requestor)) {
        modal.confirm({
          title: "Discard identity configuration?",
          content:
            "Switching identity type removes the values you've configured.",
          okText: "Discard",
          okType: "danger",
          cancelText: "Keep editing",
          onOk: apply,
        });
        return;
      }
      apply();
    },
    [modal, requestor, setRequestor]
  );

  const handleUserSubKindChange = useCallback(
    (next: UserSubKind) => setRequestor(newRequestorRuleOfType(next)),
    [setRequestor]
  );

  const handleUserChange = useCallback(
    (next: Extract<AgenticUser, { type: "group" } | { type: "user" }>) =>
      setRequestor(next),
    [setRequestor]
  );

  const handleAgenticChange = useCallback(
    (next: AgenticRequestorRule) => setRequestor(next),
    [setRequestor]
  );

  const handleWifChange = useCallback(
    (next: WifRequestorRule) => setRequestor(next),
    [setRequestor]
  );

  return (
    <Flex vertical gap={12} className="w-full">
      <FormRow>
        <RowLabel>Identity</RowLabel>
        <PillSelect<IdentityKind>
          value={identityKind}
          options={IDENTITY_OPTIONS}
          onChange={handleIdentityChange}
        />
      </FormRow>

      {identityKind === "user" &&
      requestor &&
      (requestor.type === "user" || requestor.type === "group") ? (
        <FormRow>
          <RowLabel>Match by</RowLabel>
          <PillSelect<UserSubKind>
            value={requestor.type}
            options={USER_SUB_OPTIONS}
            onChange={handleUserSubKindChange}
          />
          <UserInlineEditor
            value={requestor}
            onChange={handleUserChange}
            setRequestorError={setRequestorError}
          />
        </FormRow>
      ) : identityKind === "agentic" && requestor?.type === "agentic" ? (
        <AgenticEditor
          value={requestor}
          onChange={handleAgenticChange}
          setRequestorError={setRequestorError}
        />
      ) : identityKind === "wif" && requestor?.type === "wif" ? (
        <WifEditor
          value={requestor}
          onChange={handleWifChange}
          setRequestorError={setRequestorError}
        />
      ) : null}
    </Flex>
  );
};
